import React, { useState, useEffect } from "react";
import { useUserTaskContext } from "../contexts/UserTaskContext";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import OverlayLoader from "./OverlayLoader";
import {
  fetchProjectData,
  GetAllFollowUpTypes,
  createUserTask,
} from "../Services/APIService";

interface UpdatedUserTaskComponentProps {
  tenantId: number;
  leadId?: number;
  assignedUser?: number;
  isCompleted?: boolean;
  followupDate?: string;
  isOpen: boolean;
  actionType?: string;
  onClose: () => void;
  onTaskUpdated?: () => void;
}

interface TaskStatus {
  value: string;
  label: string;
}

const taskStatusOptions: TaskStatus[] = [
  { value: "postponed", label: "Postponed" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

interface BindtaskData {
  LeadNameLogo: string;
  LeadName: string;
  LeadEmail: string;
  LeadMobile: string;
  followupTypeName?: string;
  followupDate?: string;
  projectName?: string;
  reminderDate?: string;
  taskNotes?: string;
  assignedUser?: string;
}

interface Project {
  projectID: number;
  projectName: string;
  isActive: boolean;
}

interface ProjectOption {
  value: number;
  label: string;
}

interface FollowupType {
  id: number;
  name: string;
  isActive: boolean;
}

interface FollowupTypeOption {
  value: number;
  label: string;
}

interface ValidationErrors {
  followupDate?: string;
  followupType?: string;
  followupRemarks?: string;
}

const UpdatedUserTaskComponent: React.FC<UpdatedUserTaskComponentProps> = ({
  tenantId,
  leadId,
  assignedUser,
  isCompleted,
  followupDate,
  actionType,
  isOpen,
  onClose,
  onTaskUpdated,
}) => {
  console.log(
    "Component Props:",
    tenantId,
    leadId,
    assignedUser,
    isCompleted,
    followupDate,
    actionType,
    isOpen
  );

  const { tasks, isLoading, error, fetchTasks, updateTask, refreshTasks } =
    useUserTaskContext();

  const [editingTask, setEditingTask] = useState<any>(null);
  const [taskUpdates, setTaskUpdates] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bindtaskData, setBindtaskData] = useState<BindtaskData>({
    LeadNameLogo: "",
    LeadName: "",
    LeadEmail: "",
    LeadMobile: "",
    followupTypeName: "",
    followupDate: "",
    projectName: "",
    reminderDate: "",
    taskNotes: "",
    assignedUser: "",
  });

  // New state for follow-up scheduling
  const [scheduleFollowup, setScheduleFollowup] = useState(false);
  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: 0,
    followupProject: 0,
    followupRemarks: "",
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [followupTypes, setFollowupTypes] = useState<FollowupType[]>([]);
  const [followupTypeOptions, setFollowupTypeOptions] = useState<
    FollowupTypeOption[]
  >([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingFollowupTypes, setIsLoadingFollowupTypes] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  // Fetch projects and followup types on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!isOpen) return;

      setIsLoadingProjects(true);
      setIsLoadingFollowupTypes(true);

      try {
        // Fetch projects
        const projectAllData = await fetchProjectData();
        const projectData = projectAllData.filter(
          (project) => project.isActive
        );
        setProjects(projectData);
        const projectOptions = projectData.map((project) => ({
          value: project.projectID,
          label: project.projectName,
        }));
        setProjectOptions(projectOptions);

        // Fetch followup types
        const followupAllData = await GetAllFollowUpTypes();
        const followupData = followupAllData.filter((type) => type.isActive);
        setFollowupTypes(followupData);
        const followupOptions = followupData
          .filter((type) => type.id !== 0 && type.id !== 1) // Filter out IDs 0 and 1
          .map((type) => ({
            value: type.id,
            label: type.name,
          }));
        setFollowupTypeOptions(followupOptions);
      } catch (error) {
        console.error("Failed to fetch dropdown data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingProjects(false);
        setIsLoadingFollowupTypes(false);
      }
    };

    fetchDropdownData();
  }, [isOpen]);

  // Fetch tasks when component mounts or parameters change
  useEffect(() => {
    console.log("Fetching follow-up date:", followupDate);
    if (isOpen && tenantId) {
      fetchTasks({
        tenantId,
        leadId,
        assignedUser,
        isCompleted,
        followupDate,
      });
    }
  }, [
    tenantId,
    leadId,
    assignedUser,
    isCompleted,
    followupDate,
    fetchTasks,
    isOpen,
  ]);

  // Auto-select the first task when tasks are loaded
  useEffect(() => {
    if (tasks.length > 0 && !editingTask) {
      const taskToEdit = leadId
        ? tasks.find((task) => task.leadId === leadId)
        : tasks[0];
      if (taskToEdit) {
        handleEditTask(taskToEdit);
      }
    }

    if (editingTask != null) {
      const nameParts = editingTask.leadName?.trim().split(" ");
      const firstInitial = nameParts?.[0]?.charAt(0) || "";
      const lastInitial = nameParts?.[1]?.charAt(0) || "";
      const initials = `${firstInitial}${lastInitial}`.toUpperCase();

      setBindtaskData({
        LeadNameLogo: initials,
        LeadName: editingTask.leadName || "",
        LeadEmail: editingTask.leadEmail || "",
        LeadMobile: editingTask.leadMobileNo || "",
        followupTypeName: editingTask.followupTypeName || "",
        followupDate: editingTask.followupDate
          ? new Date(editingTask.followupDate).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          : "",
        projectName: editingTask.projectName || "",
        reminderDate: editingTask.reminderDate
          ? new Date(editingTask.reminderDate).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          : "",
        taskNotes: editingTask.taskNotes || "",
        assignedUser: editingTask.assignedUserName || "",
      });
      if (actionType) {
        setTaskUpdates({
          taskNotes: editingTask.taskNotes || "",
          postponeReason: editingTask.postponeReason || "",
          rescheduleDate: editingTask.rescheduleDate || "",
          taskStatus: actionType,
        });
      }
    }
  }, [tasks, leadId, editingTask]);

  const handleEditTask = (task: any) => {
    setEditingTask(task);
    setTaskUpdates({
      taskNotes: task.taskNotes || "",
      postponeReason: task.postponeReason || "",
      rescheduleDate: task.rescheduleDate || "",
      taskStatus: actionType || getTaskStatus(task),
    });
  };

  const getTaskStatus = (task: any): string => {
    if (task.isCompleted) return "completed";
    if (task.isCanceled) return "canceled";
    if (task.isPostponed) return "postponed";
    return "active";
  };

  const handleStatusChange = (status: string) => {
    setTaskUpdates((prev: any) => ({
      ...prev,
      taskStatus: status,
    }));

    // Reset follow-up scheduling when status changes
    if (status !== "canceled") {
      setScheduleFollowup(false);
      setFollowupData({
        followupDate: "",
        followupType: 0,
        followupProject: 0,
        followupRemarks: "",
      });
      setValidationErrors({});
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === "scheduleFollowup") {
        setScheduleFollowup(checked);
        if (!checked) {
          setFollowupData({
            followupDate: "",
            followupType: 0,
            followupProject: 0,
            followupRemarks: "",
          });
          setValidationErrors({});
        }
      }
    } else {
      setTaskUpdates((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFollowupInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    let parsedValue: any = value;

    if (["followupType", "followupProject"].includes(name)) {
      parsedValue = value === "" ? 0 : parseInt(value, 10);
    }

    setFollowupData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));

    // Clear validation error for this field
    setValidationErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleFollowupSelectChange = (name: string, selectedOption: any) => {
    const value = selectedOption ? selectedOption.value : 0;
    setFollowupData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    setValidationErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDateTime = new Date(e.target.value);
    const currentDateTime = new Date();

    if (selectedDateTime < currentDateTime) {
      toast.error("Please select a future date and time");
      return;
    }

    handleFollowupInputChange(e);
  };

  const validateFollowupForm = (): boolean => {
    if (!scheduleFollowup) return true;

    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!followupData.followupDate.trim()) {
      newErrors.followupDate = "Follow-up date is required";
      isValid = false;
    }

    if (!followupData.followupType) {
      newErrors.followupType = "Follow-up type is required";
      isValid = false;
    }

    if (!followupData.followupRemarks.trim()) {
      newErrors.followupRemarks = "Follow-up remarks is required";
      isValid = false;
    }

    setValidationErrors(newErrors);
    return isValid;
  };

  const getSelectedOption = (options: any[], value: number) => {
    return options.find((option) => option.value === value) || null;
  };

  const toNullableNumber = (value: number): number | null => {
    return value === 0 ? null : value;
  };

  const toNullableString = (value: string): string | null => {
    return value.trim() === "" ? null : value.trim();
  };

  const handleSubmit = async () => {
    if (!editingTask) return;

    if (!validateFollowupForm()) {
      toast.error("Please fill all required follow-up fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const tenantId = localStorage.getItem("tenantId");
      const userId = parseInt(localStorage.getItem("UserId") || "0", 10);

      const updates = {
        id: editingTask.id,
        tenantId: parseInt(tenantId?.toString() || "0", 10),
        leadId: editingTask.leadId,
        projectId: editingTask.projectId,
        assignedUser: editingTask.assignedUser,
        followupTypeId: editingTask.followupTypeId,
        followupDate: editingTask.followupDate,
        taskNotes: taskUpdates.taskNotes || editingTask.taskNotes || "",
        isPostponed: taskUpdates.taskStatus === "postponed",
        postponeReason: taskUpdates.postponeReason || "",
        rescheduleDate:
          taskUpdates.rescheduleDate || editingTask.rescheduleDate || null,
        reminderDate: editingTask.reminderDate,
        isCompleted: taskUpdates.taskStatus === "completed",
        isCanceled: taskUpdates.taskStatus === "canceled",
        taskCompletionDate:
          taskUpdates.taskStatus === "completed"
            ? new Date().toISOString()
            : editingTask.taskCompletionDate || null,
        modifiedBy: userId,
      };

      // Prepare follow-up data if task is canceled and follow-up is scheduled
      let followupPayload = null;
      if (taskUpdates.taskStatus === "canceled" && scheduleFollowup) {
        // Check if required follow-up fields are not null/empty
        if (
          followupData.followupDate &&
          followupData.followupType &&
          followupData.followupRemarks.trim()
        ) {
          followupPayload = {
            leadId: editingTask.leadId,
            logId: 0,
            projectId: toNullableNumber(followupData.followupProject) || 0,
            assignedUser: editingTask.assignedUser,
            followupTypeId: followupData.followupType,
            followupDate: followupData.followupDate,
            taskNotes: toNullableString(followupData.followupRemarks) || "",
            tenantId: parseInt(tenantId?.toString() || "0", 10),
            createdBy: userId,
          };
        }
      }

      // Call updateTask with followup data if available
      await updateTask(editingTask.id, updates, followupPayload);

      // Show success message
      if (
        taskUpdates.taskStatus === "canceled" &&
        scheduleFollowup &&
        followupPayload
      ) {
        toast.success("Task updated and follow-up scheduled successfully!");
      } else {
        toast.success("Task updated successfully!");
      }

      setEditingTask(null);
      setTaskUpdates({});
      setScheduleFollowup(false);
      setFollowupData({
        followupDate: "",
        followupType: 0,
        followupProject: 0,
        followupRemarks: "",
      });

      if (onTaskUpdated) {
        onTaskUpdated();
      }
      await refreshTasks();
      onClose();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleClose = () => {
    setEditingTask(null);
    setTaskUpdates({});
    setScheduleFollowup(false);
    setFollowupData({
      followupDate: "",
      followupType: 0,
      followupProject: 0,
      followupRemarks: "",
    });
    setValidationErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      {/* Modal Backdrop */}
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Update Follow-up Status</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="card-body followup-status-update-card-body p-0">
                <div className="contact-container followup-leaddetails mb-3">
                  <span className="initials-circle-container">
                    <span className="initials-circle">
                      {bindtaskData.LeadNameLogo}
                    </span>
                  </span>
                  <div className="followupLead-contact-details">
                    <div className="contact-info-name">
                      {bindtaskData.LeadName} (ID: {editingTask?.leadId})
                    </div>
                    <div className="contact-info-mobile">
                      {bindtaskData.LeadMobile}
                    </div>
                    <div className="contact-info-mobile">
                      {bindtaskData.LeadEmail}
                    </div>
                  </div>
                </div>

                <div className="modal-body-content">
                  {isLoading ? (
                    <div className="d-flex justify-content-center p-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      <strong>Error:</strong> {error}
                    </div>
                  ) : tasks.length === 0 ? (
                    <div className="text-center py-4">
                      <p>No tasks found for the specified criteria.</p>
                    </div>
                  ) : (
                    <>
                      {/* Task Update Form */}
                      {editingTask && (
                        <>
                          {/* Lead Information Card */}
                          <div className="card mb-3 followup-information-card">
                            <div className="card-header bg-transparent border-bottom-0 pb-0">
                              <h6 className="card-title mb-1 fw-semibold text-primary">
                                Follow-up Information
                              </h6>
                            </div>
                            <div className="card-body followup-information-card-body p-0">
                              <ul className="mb-0">
                                <li>
                                  Project Name:{" "}
                                  <span>{bindtaskData.projectName}</span>
                                </li>
                                <li>
                                  Follow-up Type:{" "}
                                  <span>{bindtaskData.followupTypeName}</span>
                                </li>
                                <li>
                                  Follow-up Date:{" "}
                                  <span>{bindtaskData.followupDate}</span>
                                </li>
                                <li>
                                  Assigned User:{" "}
                                  <span>{bindtaskData.assignedUser}</span>
                                </li>
                                <li>
                                  Task Notes:{" "}
                                  <span>{bindtaskData.taskNotes || "N/A"}</span>
                                </li>
                              </ul>
                            </div>
                          </div>

                          {/* Task Form */}
                          <form className="mt-4">
                            <div className="row">
                              {/* Task Status Radio Buttons */}
                              <div className="col-12 mb-3 input-group-custom">
                                <label className="form-label card-title fw-semibold text-primary">
                                  Task Status
                                </label>
                                <div className="row">
                                  {taskStatusOptions.map((status) => (
                                    <div
                                      key={status.value}
                                      className="col-12 col-lg-4 mb-2"
                                    >
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          id={`taskStatus_${status.value}`}
                                          name="taskStatus"
                                          value={status.value}
                                          checked={
                                            taskUpdates.taskStatus ===
                                            status.value
                                          }
                                          onChange={(e) =>
                                            handleStatusChange(e.target.value)
                                          }
                                          disabled={
                                            !!actionType &&
                                            actionType !== status.value
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`taskStatus_${status.value}`}
                                        >
                                          <strong>{status.label}</strong>
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Postponed Date and Reason Inputs */}
                              {taskUpdates.taskStatus === "postponed" && (
                                <>
                                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                                    <label
                                      htmlFor="rescheduleDate"
                                      className="form-label"
                                    >
                                      Reschedule Date
                                    </label>
                                    <div className="input-with-icon">
                                      <input
                                        type="datetime-local"
                                        id="rescheduleDate"
                                        name="rescheduleDate"
                                        className="form-control-custom form-control-custom1"
                                        value={taskUpdates.rescheduleDate}
                                        onChange={handleInputChange}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                              {/* Task Notes/Remarks Textarea */}
                              <div className="col-12 mb-2 input-group-custom">
                                <label
                                  htmlFor="taskNotes"
                                  className="form-label"
                                >
                                  Remarks/Notes
                                </label>
                                <div className="input-with-icon">
                                  <span className="input-icon icon-note"></span>
                                  <textarea
                                    id="postponeReason"
                                    name="postponeReason"
                                    className="form-control-custom-textarea"
                                    rows={4}
                                    placeholder="Enter task notes or remarks"
                                    value={taskUpdates.postponeReason || ""}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-12 mb-3 input-group-custom"></div>
                              </div>

                              {/* Schedule Follow-up Section for Canceled Status */}
                              {taskUpdates.taskStatus === "canceled" && (
                                <>
                                  <div className="col-12 mb-2">
                                    <h6 className="text-primary fw-bolder">
                                      Schedule Follow-up (Optional)
                                    </h6>
                                  </div>

                                  <div className="col-12 mb-3">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="scheduleFollowup"
                                        name="scheduleFollowup"
                                        checked={scheduleFollowup}
                                        onChange={handleInputChange}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="scheduleFollowup"
                                      >
                                        Schedule a follow-up
                                      </label>
                                    </div>
                                  </div>

                                  {scheduleFollowup && (
                                    <>
                                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                                        <label
                                          htmlFor="followupDate"
                                          className="form-label"
                                        >
                                          Follow-up Date & Time{requiredField}
                                        </label>
                                        <input
                                          type="datetime-local"
                                          id="followupDate"
                                          name="followupDate"
                                          value={followupData.followupDate}
                                          onChange={handleDateTimeChange}
                                          min={new Date()
                                            .toISOString()
                                            .slice(0, 16)}
                                          className={`form-control-custom1 form-control-custom-lg ${validationErrors.followupDate
                                              ? "is-invalid"
                                              : ""
                                            }`}
                                        />
                                        {validationErrors.followupDate && (
                                          <div className="invalid-feedback text-danger">
                                            {validationErrors.followupDate}
                                          </div>
                                        )}
                                      </div>
                                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                                        <label
                                          htmlFor="followupType"
                                          className="form-label"
                                        >
                                          Follow-up Type{requiredField}
                                        </label>
                                        <Select
                                          options={followupTypeOptions}
                                          value={getSelectedOption(
                                            followupTypeOptions,
                                            followupData.followupType
                                          )}
                                          onChange={(option) =>
                                            handleFollowupSelectChange(
                                              "followupType",
                                              option
                                            )
                                          }
                                          className={`basic-single-select ${validationErrors.followupType
                                              ? "is-invalid"
                                              : ""
                                            }`}
                                          classNamePrefix="select"
                                          placeholder="Select Follow-up Type"
                                          isSearchable
                                          isClearable
                                          isLoading={isLoadingFollowupTypes}
                                        />
                                        {validationErrors.followupType && (
                                          <div className="invalid-feedback text-danger">
                                            {validationErrors.followupType}
                                          </div>
                                        )}
                                      </div>

                                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                                        <label
                                          htmlFor="followupProject"
                                          className="form-label"
                                        >
                                          Project
                                        </label>
                                        <Select
                                          options={projectOptions}
                                          value={getSelectedOption(
                                            projectOptions,
                                            followupData.followupProject
                                          )}
                                          onChange={(option) =>
                                            handleFollowupSelectChange(
                                              "followupProject",
                                              option
                                            )
                                          }
                                          className="basic-single-select"
                                          classNamePrefix="select"
                                          placeholder="Select Project"
                                          isSearchable
                                          isClearable
                                          isLoading={isLoadingProjects}
                                        />
                                      </div>

                                      <div className="col-12 mb-3 input-group-custom">
                                        <label
                                          htmlFor="followupRemarks"
                                          className="form-label"
                                        >
                                          Follow-up Remarks{requiredField}
                                        </label>
                                        <textarea
                                          id="followupRemarks"
                                          name="followupRemarks"
                                          placeholder="Enter follow-up remarks"
                                          value={followupData.followupRemarks}
                                          onChange={handleFollowupInputChange}
                                          className={`form-control-custom-textarea ${validationErrors.followupRemarks
                                              ? "is-invalid"
                                              : ""
                                            }`}
                                          rows={3}
                                        />
                                        {validationErrors.followupRemarks && (
                                          <div className="invalid-feedback text-danger">
                                            {validationErrors.followupRemarks}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </form>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !editingTask}
                >
                  {isSubmitting ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoading}
          message="Loading User Task Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default UpdatedUserTaskComponent;
